/* ──────────────────────────────────────────────────────────────────────────
 * Public Landing Page — light/lavender redesign (1:1 port of
 * `Claude - ipo_labs_landing_website_v3.html`).
 *
 * The VISUAL/LAYOUT changed completely to the new reference, but every link,
 * route and piece of logic is preserved from the previous landing page:
 *   • Nav  → /advisors, /resources, /pricing, /about, /careers
 *   • Login / Get Started → /login
 *   • Module cards → /login?module=<id> (same gateways as before)
 *   • "Talk to our team" → ContactLeadDialog (sales) — same contact-us logic
 *   • Footer → same destinations (modules /login?module, /about, /careers,
 *     /resources, /disclaimer for Legal/Privacy/Terms/SEBI/Data Protection)
 *   • CookieConsent retained.
 * Styles live in scoped `LandingPageV3.css` (`.lpv3`). Tabler icons + Playfair/
 * DM Sans are loaded in public/index.html.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import ContactLeadDialog from "@/components/ContactLeadDialog";
import CookieConsent from "@/components/CookieConsent";
import "./LandingPageV3.css";

const NAV_LINKS = [
  { label: "Advisors", path: "/advisors" },
  { label: "Resources", path: "/resources" },
  { label: "IPO FAQ's", path: "/ipo-faq", testid: "nav-ipo-faq" },
  { label: "Pricing", path: "/pricing" },
  { label: "About Us", path: "/about" },
  { label: "Careers", path: "/careers" },
  { label: "Reseller Program", path: "/reseller-network" },
];

const MODULES = [
  { icon: "ti-layout-dashboard", title: "IPO Journey Dashboard", step: "Your command centre", desc: "A 6-stage tracker with readiness score, tasks and health metrics for your entire listing.", to: "/login" },
  { icon: "ti-users", title: "Advisor Marketplace", step: "Build your team", desc: "Discover and engage verified CAs, CS, legal, auditors, RTAs and bankers — matched by fit and proximity.", to: "/login?module=matchmaker" },
  { icon: "ti-file-text", title: "DRHP Workspace", step: "Draft together", desc: "Co-draft your prospectus with tracked changes, comments and SEBI observation tracking — export to Word.", to: "/login?module=drhp" },
  { icon: "ti-files", title: "Document Vault", step: "Stay compliant", desc: "Every SEBI-mandated document, tracked by status, versioned, and shared securely with your team.", to: "/login?module=drhp" },
  { icon: "ti-coins", title: "IPO Funding Desk", step: "Raise capital", desc: "Pre-IPO, bridge and post-IPO funding from anchors, banks, NBFCs, VCs and family offices.", to: "/login?module=funding" },
  { icon: "ti-chart-line", title: "Valuation Studio", step: "Know your worth", desc: "Model your valuation against listed peers, simulate price bands and size your issue.", to: "/login?module=valuation" },
];

const LandingPage = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const modulesRef = useRef(null);

  const openContact = () => setContactOpen(true);

  return (
    <div className="lpv3 lp-home" data-testid="landing-page">
      <Seo
        title="IPOLabs | India's End-to-End IPO Platform for DRHP, Advisors & Funding"
        description="Take your company public with IPOLabs — India's unified platform for IPO readiness, verified advisors, DRHP drafting, funding and NSE/BSE listing."
        path="/"
      />
      <p className="sr-only">
        IPOLabs — India&apos;s end-to-end IPO management platform. Guides founders, promoters and CFOs
        through readiness, advisors, DRHP, funding and listing, powered by the Oracle {"{IPO.GPT}"} AI tutor.
      </p>

      {/* HEADER */}
      <div className="hd" data-testid="landing-header">
        <div className="hd-inner">
          <Link to="/"><img src="/ipolabs-logo.png" alt="IPOLabs" className="brand-logo" data-testid="landing-logo-btn" /></Link>
          <div className="nav" data-testid="landing-nav">
            {NAV_LINKS.map((l) => (
              <Link key={l.path} to={l.path} data-testid={l.testid || `nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>{l.label}</Link>
            ))}
          </div>
          <div className="hd-cta">
            <a className="hd-login" href="/login" target="_blank" rel="noopener noreferrer" data-testid="landing-login-link">Login</a>
            <a className="btn-purple" href="/login" target="_blank" rel="noopener noreferrer" data-testid="landing-get-started-btn">Get Started →</a>
          </div>
        </div>
      </div>

      {/* HERO + WHY-IPOLABS share one faint grid backdrop that fades downward */}
      <div className="hero-grid-wrap">
        <div className="hero-grid-layer" aria-hidden="true" />

      {/* HERO — 2-column: headline & copy left, interactive cards right */}
      <div className="hero hero-split" data-testid="landing-hero">
        <div className="hnsv hnsv-2col">
          {/* LEFT — headline & copy */}
          <div className="hnsv-left">
            <span className="hnsv-badge">BUILD DRHP&nbsp;&nbsp;|&nbsp;&nbsp;RAISE FUNDS&nbsp;&nbsp;|&nbsp;&nbsp;HIRE IPO EXPERTS</span>
            <h1 className="hnsv-h">Going to IPO - Has never been, so easy!!!</h1>
            <div className="hnsv-sub" data-testid="landing-hero-subhead">with India&apos;s First End-to-End IPO Management Platform</div>
            <p className="hnsv-desc">IPOLabs helps Founders &amp; CA Firms to Plan an IPO, Track IPO Readiness, Draft DRHP&apos;s, Consult or Hire IPO Experts, Track Costs and Raise Funds - All in one easy-to-use <span className="accent">PLATFORM</span>!</p>
            <div className="hnsv-trust">
              <span><i className="ti ti-circle-check" aria-hidden="true" /> 6-stage guided journey</span>
              <span><i className="ti ti-circle-check" aria-hidden="true" /> SEBI-aligned</span>
              <span><i className="ti ti-circle-check" aria-hidden="true" /> Built for both sides</span>
            </div>
          </div>

          {/* RIGHT — interactive cards (existing content preserved) */}
          <div className="hnsv-right">
            <div className="hv4-grid">
              {/* Founders */}
              <div className="hv4-card">
                <div className="hv4-panel">
                  <div className="hv4-head">
                    <div className="hv4-ic hv4-ic-1"><i className="ti ti-rocket" aria-hidden="true" /></div>
                    <div className="hv4-eyebrow">BUILD YOUR IPO · FOUNDERS</div>
                  </div>
                  <h3 className="hv4-title">Plan, manage &amp; run your IPO</h3>
                  <p className="hv4-points">Track IPO Readiness Tracking, Hire Verified IPO Experts, Build DRHP, Manage IPO End-to-End</p>
                  <div className="hv4-spacer" />
                  <a className="hv4-btn" href="/login" target="_blank" rel="noopener noreferrer" data-testid="hero-login-founder-btn"><i className="ti ti-rocket" aria-hidden="true" /> Login as a Founder</a>
                </div>
              </div>

              {/* CA Firms */}
              <div className="hv4-card">
                <div className="hv4-panel">
                  <div className="hv4-head">
                    <div className="hv4-ic hv4-ic-2"><i className="ti ti-building-bank" aria-hidden="true" /></div>
                    <div className="hv4-eyebrow">BUILD YOUR IPO · CA FIRMS</div>
                  </div>
                  <h3 className="hv4-title">Track, evaluate &amp; rank clients</h3>
                  <p className="hv4-points">Manage Multiple IPO Clients, Track Client Progress, Partner with Advisors, Join our Reseller Program</p>
                  <div className="hv4-spacer" />
                  <a className="hv4-btn" href="/login" target="_blank" rel="noopener noreferrer" data-testid="hero-login-cafirm-btn">Login as a CA Firm</a>
                </div>
              </div>

              {/* IPO Advisor */}
              <div className="hv4-card">
                <div className="hv4-panel">
                  <div className="hv4-head">
                    <div className="hv4-ic hv4-ic-3"><i className="ti ti-user-star" aria-hidden="true" /></div>
                    <div className="hv4-eyebrow">FOR IPO ADVISORS</div>
                  </div>
                  <h3 className="hv4-title">Register &amp; win IPO mandates</h3>
                  <p className="hv4-points">Join as Individual or Firm, Match with MSME Clients, Get Verified Mandates, Pan-India Reach</p>
                  <div className="hv4-spacer" />
                  <a className="hv4-btn" href="/advisor-onboarding" data-testid="hero-login-advisor-btn"><i className="ti ti-user-star" aria-hidden="true" /> Login as an Advisor</a>
                </div>
              </div>

              {/* Raise Funds */}
              <div className="hv4-card">
                <div className="hv4-panel">
                  <div className="hv4-head">
                    <div className="hv4-ic hv4-ic-4"><i className="ti ti-coins" aria-hidden="true" /></div>
                    <div className="hv4-eyebrow">RAISE FUNDS</div>
                  </div>
                  <h3 className="hv4-title">Fund every stage of growth</h3>
                  <p className="hv4-points">IPO Capital, Debt Financing, Bridge Loans, Working Capital</p>
                  <div className="hv4-spacer" />
                  <a className="hv4-btn" href="/funding" target="_blank" rel="noopener noreferrer" data-testid="hero-explore-funding-btn"><i className="ti ti-coins" aria-hidden="true" /> Explore Funding</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* VALUE PROPS */}
      <div className="sec why-sec">
        <div className="sec-kick why-ipolabs">Why IPO<span>Labs</span></div>
        <div className="sec-h why-ipolabs-h">Built by <span className="accent">Founders</span> who have faced the IPO Journey</div>
        <div className="sec-d">Most Founders, look at IPO Process as a &ldquo;Black Box&rdquo;, where its common to let Merchant Bankers manage the process. At IPOLabs, we believe &ldquo;Founders&rdquo; can and should lead the journey, and have control of every step of the journey.</div>
        <div className="vals">
          <div className="val">
            <div className="val-ic"><i className="ti ti-route" aria-hidden="true" /></div>
            <div className="val-h">Full clarity, every step</div>
            <div className="val-d">A 6-stage roadmap shows exactly where you are, what&apos;s next, and what each step means — no more flying blind through your own IPO.</div>
          </div>
          <div className="val">
            <div className="val-ic"><i className="ti ti-steering-wheel" aria-hidden="true" /></div>
            <div className="val-h">Founders in control</div>
            <div className="val-d">Pick your own advisors, draft your own DRHP, compare your own funding. The platform empowers you instead of leaving you dependent.</div>
          </div>
          <div className="val">
            <div className="val-ic"><i className="ti ti-clock-bolt" aria-hidden="true" /></div>
            <div className="val-h">Faster, simpler, cheaper</div>
            <div className="val-d">Verified advisors, reusable KYC, AI-assisted drafting and a built-in funding desk cut the cost and time of getting to the exchange.</div>
          </div>
        </div>
      </div>
      </div>{/* /hero-grid-wrap */}

      {/* JOIN OUR INDIA RESELLER PROGRAM — reseller carousel (mirrors the hero) */}
      <div className="hero" data-testid="landing-reseller-banner">
        <span className="hero-pill"><i className="ti ti-world-share" aria-hidden="true" /> Global Reseller Program · Pan-India</span>
        <div className="hero-h">Join our <span className="accent">India Reseller Program</span></div>
        <div className="hero-h2 reseller-h2">Are you a CA Firm managing a client-base who are IPO Ready? Let IPOLab&apos;s be your <span className="accent">Technology Partner</span>!</div>
        <div className="hero-sub hero-sub-wide reseller-sub">Refer IPOLabs and get 25% Flat on every deal closed!</div>
        <div className="hero-btns">
          <button className="btn-dark" onClick={openContact} data-testid="reseller-become-partner-btn"><i className="ti ti-rocket" aria-hidden="true" /> Become a Partner</button>
          <Link className="btn-line" to="/reseller-network" data-testid="reseller-explore-btn">Explore the program</Link>
        </div>
      </div>


      {/* ════════════════════════════════════════════════════════════ */}
      {/* OUR TECHNOLOGY STACK — infinite marquee (ported from Fork 20)  */}
      {/* ════════════════════════════════════════════════════════════ */}
      <section className="relative z-10 bg-[#0c0c0d] border-t border-white/5 px-6 py-16 lg:py-20" data-testid="landing-tech-stack">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[13px] tracking-[0.22em] uppercase text-indigo-400 font-semibold mb-3">Our Technology Stack</p>
            <h2 className="text-[32px] lg:text-[38px] font-bold tracking-tight text-white">
              Built on the{" "}
              <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                world&apos;s most trusted infrastructure.
              </span>
            </h2>
          </div>

          {/* marquee CSS (scoped) */}
          <style>{`
            @keyframes ipolabs-marquee {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .ipolabs-marquee-track {
              animation: ipolabs-marquee 36s linear infinite;
              will-change: transform;
            }
            .ipolabs-marquee-track:hover { animation-play-state: paused; }
            .ipolabs-marquee-mask {
              -webkit-mask-image: linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%);
                      mask-image: linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%);
            }
          `}</style>

          <div className="relative overflow-hidden ipolabs-marquee-mask">
            <div className="ipolabs-marquee-track flex items-center gap-5 lg:gap-6 w-max">
              {[...Array(2)].map((_, dupIdx) => (
                <div key={dupIdx} className="flex items-center gap-5 lg:gap-6 shrink-0" aria-hidden={dupIdx === 1}>
                  {[
                    {
                      name: "Emergent.sh",
                      kind: "wordmark",
                      mark: <span className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-fuchsia-400 to-purple-500 bg-clip-text text-transparent">{"{ E }"}</span>,
                    },
                    {
                      name: "MongoDB",
                      kind: "img",
                      src: "https://cdn.simpleicons.org/mongodb",
                    },
                    {
                      name: "Microsoft",
                      kind: "img",
                      src: "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg",
                      imgClass: "h-5 lg:h-6",
                      hideName: true,
                    },
                    {
                      name: "Razorpay",
                      kind: "img",
                      src: "https://cdn.simpleicons.org/razorpay",
                    },
                    {
                      name: "Google Authenticator",
                      kind: "img",
                      src: "https://cdn.simpleicons.org/googleauthenticator",
                    },
                    {
                      name: "Google Gemini",
                      kind: "img",
                      src: "https://cdn.simpleicons.org/googlegemini",
                    },
                    {
                      name: "Claude.ai",
                      kind: "img",
                      src: "https://cdn.simpleicons.org/claude",
                    },
                    {
                      name: "Flow.ai",
                      kind: "wordmark",
                      mark: (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-6 h-6 rounded-md bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-[11px] font-black">F</span>
                        </span>
                      ),
                    },
                    {
                      name: "JavaScript",
                      kind: "img",
                      src: "https://cdn.simpleicons.org/javascript",
                    },
                  ].map((logo) => (
                    <div
                      key={`${dupIdx}-${logo.name}`}
                      className="flex items-center gap-3 px-5 lg:px-6 py-3 lg:py-3.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] hover:border-white/20 transition-colors shrink-0 backdrop-blur-sm"
                      title={logo.name}
                    >
                      {logo.kind === "img" ? (
                        <img
                          src={logo.src}
                          alt={`${logo.name} logo`}
                          className={logo.imgClass || "h-6 lg:h-7 w-auto"}
                          loading="lazy"
                        />
                      ) : (
                        logo.mark
                      )}
                      {!logo.hideName && (
                        <span className="text-base lg:text-lg font-semibold tracking-tight text-white whitespace-nowrap">
                          {logo.name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <div className="sec alt" ref={modulesRef} data-testid="landing-modules">
        <div className="sec-kick">One platform, every piece of the IPO</div>
        <div className="sec-h">Everything you need to <span className="accent">go public</span></div>
        <div className="sec-d">Each module solves one part of the journey — and they all work together, from first thought to listing bell.</div>
        <div className="mods">
          {MODULES.map((m, i) => (
            <a key={i} className="mod" href={m.to} target="_blank" rel="noopener noreferrer" data-testid={`landing-module-${i}`}>
              <div className="mod-top">
                <div className="mod-ic"><i className={`ti ${m.icon}`} aria-hidden="true" /></div>
                <div><div className="mod-h">{m.title}</div><div className="mod-step">{m.step}</div></div>
              </div>
              <div className="mod-d">{m.desc}</div>
            </a>
          ))}
        </div>
      </div>

      {/* ORACLE — FULL BLEED */}
      <div className="oracle-full" data-testid="landing-oracle">
        <div className="oracle-head">
          <div className="sec-kick">Custom-built intelligence</div>
          <div className="sec-h">Meet Oracle {"{IPO.GPT}"}</div>
          <div className="sec-d">India&apos;s first IPO-first large language model — built by IPOLabs, trained for the Indian capital market.</div>
        </div>
        <div className="oracle-inner">
          <div>
            <div className="or-kick"><i className="ti ti-sparkles" aria-hidden="true" /> The IPO-first LLM</div>
            <div className="or-h">An AI that speaks <span>IPO</span>, fluently.</div>
            <div className="or-d">Oracle {"{IPO.GPT}"} is a custom-designed language model built by IPOLabs for Indian capital markets. It understands SEBI regulations, DRHP structure, funding instruments and listing rules — and explains them in plain language to first-time founders.</div>
            <div className="or-feats">
              <div className="or-f"><i className="ti ti-school" aria-hidden="true" /> Teaches the entire IPO journey, jargon-free</div>
              <div className="or-f"><i className="ti ti-gavel" aria-hidden="true" /> Grounded in SEBI ICDR &amp; Indian market rules</div>
              <div className="or-f"><i className="ti ti-message-2" aria-hidden="true" /> Context-aware help inside every module</div>
              <div className="or-f"><i className="ti ti-shield-check" aria-hidden="true" /> Built for the Indian capital market</div>
            </div>
          </div>
          <div className="or-card">
            <div className="or-card-logo"><i className="ti ti-sparkles" style={{ color: "#fff", fontSize: 19 }} aria-hidden="true" /></div>
            <div className="or-chat me">What is a DRHP and why do I need one?</div>
            <div className="or-chat">A DRHP is your company&apos;s full &quot;story + numbers&quot; document filed with SEBI to ask permission to go public — like an official pitch deck the regulator must approve. Want the 5-minute lesson?</div>
            <a className="or-btn" href="/login" target="_blank" rel="noopener noreferrer" data-testid="oracle-ask-btn">Ask Oracle {"{IPO.GPT}"} anything →</a>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="cta" data-testid="landing-cta">
        <div className="cta-h">Your IPO journey starts here</div>
        <div className="cta-d">Join the founders, promoters and CFOs taking control of their path to public markets — with clarity from day one.</div>
        <div className="hero-btns">
          <a className="btn-dark" href="/login" target="_blank" rel="noopener noreferrer" data-testid="cta-get-started-btn"><i className="ti ti-rocket" aria-hidden="true" /> Get Started Free</a>
          <button className="btn-line" onClick={openContact} data-testid="cta-talk-btn">Talk to our team</button>
        </div>
      </div>

      {/* FOOTER */}
      <div className="ft">
        <div className="ft-grid">
          <div>
            <img src="/ipolabs-logo.png" alt="IPOLabs" className="ft-brand-logo" />
            <div className="ft-tag">India&apos;s end-to-end IPO management platform — helping founders, promoters and CFOs go public with clarity and control.</div>
            <div className="ft-tm" data-testid="footer-trademark-notice"><span className="ft-tm-brand">IPOLabs<sup className="ft-tm-sup">TM</sup></span> is a trademark of IPO Labs AI Private Limited.</div>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Platform</div>
            <Link to="/login">IPO Journey</Link>
            <Link to="/login?module=matchmaker">Advisor Marketplace</Link>
            <Link to="/login?module=drhp">DRHP Workspace</Link>
            <Link to="/login?module=funding">Funding Desk</Link>
            <Link to="/ipo-faq" data-testid="footer-ipo-faq">IPO FAQ&apos;s</Link>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Company</div>
            <Link to="/about">About Us</Link>
            <Link to="/login">Oracle {"{IPO.GPT}"}</Link>
            <Link to="/careers">Careers</Link>
            <Link to="/login">Reseller Program</Link>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Get started</div>
            <Link to="/login">Log in</Link>
            <Link to="/login">Create workspace</Link>
            <Link to="/login">Join as advisor</Link>
            <Link to="/login">Join as investor</Link>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Legal &amp; compliance</div>
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/resources">Resources</Link>
            <Link to="/disclaimer">Privacy Policy</Link>
            <Link to="/disclaimer">Terms of Services</Link>
            <Link to="/disclaimer">SEBI Compliance</Link>
            <Link to="/disclaimer">Data Protection</Link>
          </div>
        </div>
        <div className="ft-bot">
          <span>© {new Date().getFullYear()} IPOLabs. All rights reserved.</span>
          <span>IPOLabs · India&apos;s end-to-end IPO management platform</span>
        </div>
      </div>

      <ContactLeadDialog open={contactOpen} onOpenChange={setContactOpen} leadType="sales" />
      <CookieConsent />
    </div>
  );
};

export default LandingPage;
